import type { ParseResumeResponseData, PendingLocation } from '../types';

const COUNTRY_SELECT_ID = 'application_candidate_attributes_address_attributes_country_id';
const STATE_SELECT_ID = 'application_candidate_attributes_address_attributes_state_id';
const STREET_ADDRESS_INPUT_ID = 'application_candidate_attributes_address_attributes_street1';
const STREET_NUMBER_INPUT_ID = 'application_candidate_attributes_address_attributes_street_number';
const STREET_TYPE_SELECT_ID = 'application_candidate_attributes_address_attributes_street_type';

type AutofillInput = HTMLInputElement | HTMLTextAreaElement;

export const findFileExtension = (fileName: string): string =>
  fileName.split('.').pop()?.toLowerCase().trim() ?? '';

export const hasValidValue = (value: unknown): value is string | number => {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value !== 'string') {
    return false;
  }

  const normalizedValue = value.trim().toLowerCase();
  return normalizedValue !== '' && normalizedValue !== 'undefined' && normalizedValue !== 'null';
};

export const fillInputValue = (inputId: string, value: unknown): void => {
  if (!hasValidValue(value)) {
    return;
  }

  const input = document.getElementById(inputId) as AutofillInput | null;
  if (!input) {
    return;
  }

  input.value = String(value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
};

export const fillSelectValue = (selectId: string, value: unknown): void => {
  if (!hasValidValue(value)) {
    return;
  }

  const select = document.getElementById(selectId) as HTMLSelectElement | null;
  if (!select) {
    return;
  }

  const nextValue = String(value);
  const matchingOption = Array.from(select.options).find(
    (option) => option.value?.toLowerCase() === nextValue.toLowerCase()
  );
  if (!matchingOption || select.value === matchingOption.value) {
    return;
  }

  select.value = matchingOption.value;
  select.dispatchEvent(new Event('change', { bubbles: true }));
};

export const fillOrClearSelectValue = (selectId: string, value: unknown): void => {
  const select = document.getElementById(selectId) as HTMLSelectElement | null;
  if (!select) {
    return;
  }

  const nextValue = hasValidValue(value) ? String(value) : '';
  if (select.value === nextValue) {
    return;
  }

  select.value = nextValue;
  select.dispatchEvent(new Event('change', { bubbles: true }));
};

export const fillCountryThenState = (countryId: unknown, stateId: unknown): void => {
  const stateSelect = document.getElementById(STATE_SELECT_ID) as HTMLSelectElement | null;

  if (!hasValidValue(countryId)) {
    fillOrClearSelectValue(COUNTRY_SELECT_ID, '');
    fillOrClearSelectValue(STATE_SELECT_ID, '');
    return;
  }

  if (!stateSelect) {
    fillOrClearSelectValue(COUNTRY_SELECT_ID, countryId);
    fillOrClearSelectValue(STATE_SELECT_ID, stateId);
    return;
  }

  const applyStateValue = () => {
    if (!hasValidValue(stateId)) {
      fillOrClearSelectValue(STATE_SELECT_ID, '');
      return true;
    }

    const matchingOption = Array.from(stateSelect.options).some(
      (option) => option.value === String(stateId)
    );
    if (!matchingOption) {
      return false;
    }

    stateSelect.value = String(stateId);
    stateSelect.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  const observer = new MutationObserver(() => {
    if (applyStateValue()) {
      observer.disconnect();
    }
  });

  observer.observe(stateSelect, { childList: true });
  fillOrClearSelectValue(COUNTRY_SELECT_ID, countryId);

  if (applyStateValue()) {
    observer.disconnect();
    return;
  }

  window.setTimeout(() => observer.disconnect(), 500);
};

export const isAsciiLetter = (value: string): boolean => {
  if (value.length !== 1) {
    return false;
  }

  const charCode = value.charCodeAt(0);
  return (
    (charCode >= 'A'.charCodeAt(0) && charCode <= 'Z'.charCodeAt(0)) ||
    (charCode >= 'a'.charCodeAt(0) && charCode <= 'z'.charCodeAt(0))
  );
};

export const isNumericWithOptionalLetterSuffix = (value: string): boolean => {
  if (!value) {
    return false;
  }

  const chars = Array.from(value);
  const suffix = chars[chars.length - 1] ?? '';
  const numericPart = isAsciiLetter(suffix) ? chars.slice(0, -1) : chars;

  return (
    numericPart.length > 0 &&
    numericPart.every((char) => char >= '0' && char <= '9') &&
    (!isAsciiLetter(suffix) || chars.length > 1)
  );
};

export const isStreetNumberToken = (value: string): boolean => {
  const segments = value.split('/');
  return (
    segments.length > 0 &&
    segments.length <= 2 &&
    segments.every((segment) => isNumericWithOptionalLetterSuffix(segment))
  );
};

export const fillStreetAddress = (streetAddress: unknown): void => {
  const streetNumberInput = document.getElementById(
    STREET_NUMBER_INPUT_ID
  ) as HTMLInputElement | null;
  const streetTypeSelect = document.getElementById(
    STREET_TYPE_SELECT_ID
  ) as HTMLSelectElement | null;

  if (!streetNumberInput || !streetTypeSelect) {
    fillInputValue(STREET_ADDRESS_INPUT_ID, streetAddress);
    return;
  }

  if (!hasValidValue(streetAddress)) {
    return;
  }

  const normalizedAddress = String(streetAddress).trim();
  const parts = normalizedAddress.split(/\s+/).filter(Boolean);
  const firstPart = parts[0] ?? '';
  const lastPart = parts[parts.length - 1] ?? '';
  const streetNumber = isStreetNumberToken(firstPart) ? firstPart : '';
  const streetType =
    Array.from(streetTypeSelect.options).find(
      (option) => option.value?.toLowerCase() === lastPart.toLowerCase()
    )?.value ?? '';

  let streetName = normalizedAddress;
  if (streetNumber && streetType && parts.length > 2) {
    streetName = parts.slice(1, -1).join(' ');
  } else if (streetNumber && parts.length > 1) {
    streetName = parts.slice(1).join(' ');
  } else if (streetType && parts.length > 1) {
    streetName = parts.slice(0, -1).join(' ');
  }

  const highConfidenceSplit = !!streetNumber && !!streetType && parts.length >= 3;

  if (highConfidenceSplit) {
    fillInputValue(STREET_NUMBER_INPUT_ID, streetNumber);
    fillInputValue(STREET_ADDRESS_INPUT_ID, streetName);
    fillSelectValue(STREET_TYPE_SELECT_ID, streetType);
    return;
  }

  fillInputValue(STREET_ADDRESS_INPUT_ID, normalizedAddress);
};

interface FillParsedFieldsOptions {
  countriesReady: boolean;
  setPendingLocation: (location: PendingLocation | null) => void;
}

export const fillParsedFields = (
  data: ParseResumeResponseData,
  options: FillParsedFieldsOptions
): void => {
  fillInputValue('application_candidate_attributes_firstname', data.first_name);
  fillInputValue('application_candidate_attributes_lastname', data.last_name);
  fillInputValue('application_candidate_attributes_email', data.email);
  fillInputValue('application_candidate_attributes_phone1', data.phone);
  fillInputValue('application_candidate_attributes_phone2', data.mobile);
  fillStreetAddress(data.street_address);
  fillInputValue('application_candidate_attributes_address_attributes_suburb', data.city);
  fillInputValue('application_candidate_attributes_address_attributes_postcode', data.postcode);

  if (!hasValidValue(data.country_id)) {
    options.setPendingLocation(null);
    return;
  }

  if (options.countriesReady) {
    options.setPendingLocation(null);
    fillCountryThenState(data.country_id, data.state_id);
    return;
  }

  options.setPendingLocation({
    countryId: data.country_id,
    stateId: data.state_id
  });
};
